import React from "react";
import { User } from "../../__generated__/graphql";
import Constants from "expo-constants";
import { useIntercom as useRootIntercom } from "react-use-intercom";
import { ArticleId, getArticleId } from "./helpers";
import { TrackingEventName } from "../tracking";
import { Content, UserAttributes } from "@intercom/intercom-react-native";

const isIntercomEnabled = () => {
    return !!Constants.expoConfig?.extra?.intercomAppId;
};

export const useIntercom = () => {
    const {
        shutdown,
        update,
        showArticle,
        trackEvent: trackIntercomEvent,
        show,
        showNewMessage,
    } = useRootIntercom();
    const isUserLoggedIn = React.useRef(false);
    const presentArticle = React.useCallback(
        (articleId: ArticleId) => {
            const intercomArticleId = getArticleId(articleId);
            // eslint-disable-next-line no-console
            console.log(
                "Open intercom article",
                intercomArticleId,
                "|",
                Constants.expoConfig?.extra?.appEnv,
            );
            return showArticle(parseInt(intercomArticleId));
        },
        [showArticle],
    );

    const presentArticleId = React.useCallback(
        (articleId: string) => {
            // eslint-disable-next-line no-console
            console.log(
                "Open intercom article",
                articleId,
                "|",
                Constants.expoConfig?.extra?.appEnv,
            );
            return showArticle(parseInt(articleId));
        },
        [showArticle],
    );

    const login = React.useCallback(
        (user: User) => {
            if (!isIntercomEnabled()) {
                return;
            }
            if (!isUserLoggedIn.current && user.webIntercomHmac) {
                isUserLoggedIn.current = true;
                try {
                    update({
                        userHash: user.webIntercomHmac,
                        ...(user.intercomUserId
                            ? { email: user.email || "", userId: user.intercomUserId }
                            : {}),
                    });
                } catch (error) {
                    // todo: known issue. We don't know how remove this exception but it doesn't affect the app
                    // @see https://community.intercom.com/mobile-sdks-24/how-can-i-get-the-currently-logged-in-user-2766?postid=15332#post15332
                    //eslint-disable-next-line no-console
                    console.log("Just ignore this error from intercom", error);
                }
            }
        },
        [update],
    );

    const logout = React.useCallback(() => {
        if (!isIntercomEnabled()) {
            return;
        }
        if (isUserLoggedIn.current) {
            isUserLoggedIn.current = false;
            shutdown();
        }
    }, [shutdown]);

    const logEvent = React.useCallback(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (eventName: TrackingEventName, params?: { [key: string]: any }) => {
            if (!isIntercomEnabled()) {
                return Promise.resolve(false);
            }
            return trackIntercomEvent(eventName, params);
        },
        [trackIntercomEvent],
    );

    const presentIntercom = React.useCallback(() => {
        if (!isIntercomEnabled()) {
            // eslint-disable-next-line no-console
            console.log("Intercom is not enabled");
            return Promise.resolve(false);
        }
        return show();
    }, [show]);

    const presentMessageComposer = React.useCallback(() => {
        if (!isIntercomEnabled()) {
            // eslint-disable-next-line no-console
            console.log("Intercom is not enabled");
            return Promise.resolve(false);
        }
        return showNewMessage();
    }, [showNewMessage]);

    const loginUnidentifiedUser = React.useCallback(() => {
        console.error("loginUnidentifiedUser() is not implemented on web");
        return Promise.resolve(false);
    }, []);

    const presentContent = React.useCallback((_content: Content) => {
        console.error("presentContent() is not implemented on web");
        return Promise.resolve(false);
    }, []);

    const updateUser = React.useCallback(
        (user: UserAttributes) => {
            update({
                email: user.email,
                userId: user.userId,
                createdAt: user.signedUpAt,
                name: user.name,
                phone: user.phone,
                unsubscribedFromEmails: user.unsubscribedFromEmails,
                languageOverride: user.languageOverride,
                companies: user.companies?.map((company) => ({
                    companyId: company.id,
                    name: company.name,
                    createdAt: company.createdAt,
                    plan: company.plan,
                    monthlySpend: company.monthlySpend,
                })),
            });
        },
        [update],
    );

    return {
        login,
        loginUnidentifiedUser,
        logout,
        logEvent,
        presentIntercom,
        presentContent,
        updateUser,
        presentArticle,
        presentArticleId,
        presentMessageComposer,
    };
};
